import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Plus, Tag, Trash2, Upload, Sparkles, Search, ArrowUpDown } from "lucide-react";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const INTENT_LABEL = { informational: "Informacional", commercial: "Comercial", transactional: "Transaccional" };
const SOURCE_LABEL = { manual: "Manual", csv: "CSV", google_search_console: "Google" };
const EMPTY_FORM = { term: "", location: "", state: "", intent: "informational", search_volume: "", difficulty: "", seasonality_months: [], business_relevance: 3 };

export default function KeywordsPage() {
  const { activeTenantId } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const csvRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [kws, sug] = await Promise.all([api.get("/keywords"), api.get("/keywords/suggestions")]);
      setItems(kws.data);
      setSuggestions(sug.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId) load(); }, [activeTenantId, load]);
  useEffect(() => {
    if (activeTenantId) {
      api.get("/google/status").then((r) => setGoogleConnected(r.data?.connection_status === "connected")).catch(() => {});
    }
  }, [activeTenantId]);

  const openCreate = () => { setForm(EMPTY_FORM); setOpen(true); };

  const toggleMonth = (m) => {
    setForm((f) => ({
      ...f,
      seasonality_months: f.seasonality_months.includes(m) ? f.seasonality_months.filter((x) => x !== m) : [...f.seasonality_months, m],
    }));
  };

  const save = async () => {
    if (!form.term.trim()) return toast.error("Escribí el término");
    try {
      await api.post("/keywords", {
        ...form,
        search_volume: form.search_volume ? Number(form.search_volume) : null,
        difficulty: form.difficulty ? Number(form.difficulty) : null,
        business_relevance: Number(form.business_relevance),
      });
      toast.success("Keyword creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    }
  };

  const remove = async (kw) => {
    try {
      await api.delete(`/keywords/${kw.id}`);
      toast.success("Keyword eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/keywords/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${res.data.created} keywords importadas${res.data.errors.length ? ` (${res.data.errors.length} filas con error)` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar el CSV");
    }
  };

  const syncGoogle = async () => {
    if (!googleConnected) {
      navigate("/dashboard/connections");
      return;
    }
    setSyncingGoogle(true);
    try {
      const res = await api.post("/google/sync-keywords");
      if (res.data.total_rows === 0) {
        toast.info("Todavía no hay datos de búsqueda para este sitio");
      } else {
        toast.success(`${res.data.imported} nuevas · ${res.data.updated} actualizadas`);
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 400 && detail?.toLowerCase().includes("conexión")) {
        navigate("/dashboard/connections");
      }
      toast.error(detail || "No se pudo traer las keywords de Google");
    } finally {
      setSyncingGoogle(false);
    }
  };

  const toggleSort = (key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  };

  const sortedItems = sort.key
    ? [...items].sort((a, b) => {
        const av = a[sort.key] ?? -1;
        const bv = b[sort.key] ?? -1;
        return sort.dir === "desc" ? bv - av : av - bv;
      })
    : items;

  return (
    <div className="space-y-6" data-testid="keywords-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A] max-w-xl">
          Palabras clave con estacionalidad: en verano suben áticos, en invierno sótanos. Las sugerencias
          priorizan según el mes en curso.
        </p>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={syncGoogle} disabled={syncingGoogle} data-testid="sync-google-keywords-button" className="rounded-md">
            {syncingGoogle ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />} Traer de Google
          </Button>
          <input ref={csvRef} type="file" accept=".csv" className="hidden" data-testid="keywords-csv-input"
            onChange={(e) => importCsv(e.target.files?.[0])} />
          <Button variant="outline" size="sm" onClick={() => csvRef.current?.click()} data-testid="keywords-csv-button" className="rounded-md">
            <Upload className="h-4 w-4 mr-1" /> Importar CSV
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-keyword-button" onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">
                <Plus className="h-4 w-4 mr-1" /> Nueva keyword
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="keyword-dialog">
              <DialogHeader><DialogTitle className="font-heading">Nueva keyword</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Término</Label>
                  <Input data-testid="keyword-term-input" value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} placeholder="attic insulation cost" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#71717A]">Ubicación</Label>
                    <Input data-testid="keyword-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Brooklyn" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#71717A]">Estado</Label>
                    <Input data-testid="keyword-state-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="NY" maxLength={2} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Intención</Label>
                  <Select value={form.intent} onValueChange={(v) => setForm({ ...form, intent: v })}>
                    <SelectTrigger data-testid="keyword-intent-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(INTENT_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#71717A]">Volumen de búsqueda</Label>
                    <Input data-testid="keyword-volume-input" type="number" value={form.search_volume} onChange={(e) => setForm({ ...form, search_volume: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#71717A]">Dificultad (0-100)</Label>
                    <Input data-testid="keyword-difficulty-input" type="number" value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Relevancia de negocio (1-5)</Label>
                  <Input data-testid="keyword-relevance-input" type="number" min={1} max={5} value={form.business_relevance} onChange={(e) => setForm({ ...form, business_relevance: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Meses de estacionalidad (sube la búsqueda)</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {MONTHS.map((m, idx) => {
                      const month = idx + 1;
                      const active = form.seasonality_months.includes(month);
                      return (
                        <button type="button" key={m} onClick={() => toggleMonth(month)} data-testid={`keyword-month-${month}`}
                          className={`text-xs px-2.5 py-1 rounded-sm border ${active ? "bg-[#0055FF] text-white border-[#0055FF]" : "border-[#E4E4E7] text-[#71717A]"}`}>
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-keyword-button" onClick={save} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {suggestions.length > 0 && (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-4" data-testid="keyword-suggestions-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[#0055FF]" />
            <h3 className="font-heading text-sm font-bold">Sugerencias para este mes</h3>
          </div>
          <div className="space-y-2">
            {suggestions.slice(0, 6).map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm border-b border-[#F4F4F5] pb-2 last:border-0" data-testid={`keyword-suggestion-${s.id}`}>
                <div>
                  <span className="font-medium">{s.term}</span>
                  <span className="text-[#71717A]"> — {s.reason}</span>
                </div>
                <Badge variant="outline" className="text-[10px] rounded-sm">{s.suggested_content_type}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center px-6" data-testid="keywords-empty-state">
            <Tag className="h-8 w-8 text-[#A1A1AA]" />
            <p className="mt-3 text-sm font-medium text-[#09090B]">Todavía no cargaste keywords</p>
            <p className="mt-1 text-xs text-[#71717A] max-w-sm">
              Las keywords le dicen a la plataforma qué buscan tus clientes, para sugerir qué contenido
              generar primero.
            </p>
            <Button
              size="sm" className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
              data-testid="keywords-empty-state-button" onClick={openCreate}
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Cargar la primera
            </Button>
          </div>
        ) : (
          <Table data-testid="keywords-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Término</TableHead>
                <TableHead>Ubicación</TableHead>
                <TableHead>Intención</TableHead>
                <TableHead>Volumen</TableHead>
                <TableHead>Relevancia</TableHead>
                <TableHead>
                  <button className="flex items-center gap-1" onClick={() => toggleSort("gsc_clicks")} data-testid="sort-clicks-button">
                    Clics <ArrowUpDown className="h-3 w-3" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center gap-1" onClick={() => toggleSort("gsc_impressions")} data-testid="sort-impressions-button">
                    Impresiones <ArrowUpDown className="h-3 w-3" />
                  </button>
                </TableHead>
                <TableHead>Posición</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Cubierta</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map((k) => (
                <TableRow key={k.id} data-testid={`keyword-row-${k.id}`}>
                  <TableCell className="font-medium">{k.term}</TableCell>
                  <TableCell className="text-[#71717A]">{k.location || "-"}{k.state ? `, ${k.state}` : ""}</TableCell>
                  <TableCell className="text-[#71717A]">{INTENT_LABEL[k.intent]}</TableCell>
                  <TableCell className="text-[#71717A]">{k.search_volume || "-"}</TableCell>
                  <TableCell className="text-[#71717A]">{k.business_relevance}/5</TableCell>
                  <TableCell className="text-[#71717A]" data-testid={`keyword-clicks-${k.id}`}>{k.gsc_clicks ?? ""}</TableCell>
                  <TableCell className="text-[#71717A]" data-testid={`keyword-impressions-${k.id}`}>{k.gsc_impressions ?? ""}</TableCell>
                  <TableCell className="text-[#71717A]" data-testid={`keyword-position-${k.id}`}>{k.gsc_position != null ? k.gsc_position.toFixed(1) : ""}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] rounded-sm">{SOURCE_LABEL[k.source] || k.source}</Badge>
                  </TableCell>
                  <TableCell>
                    {k.covered ? <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 text-[10px] rounded-sm">Sí</Badge>
                      : <Badge className="bg-[#A1A1AA]/10 text-[#71717A] border-0 text-[10px] rounded-sm">No</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => remove(k)} data-testid={`delete-keyword-${k.id}`} className="text-[#EF4444]">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
